import { useCallback } from 'react';

import { useRealtimeEvent, type UserEvent } from '@/features/realtime/RealtimeProvider';
import { CLINICAL_ROLES } from '@/lib/rbac/roles';
import { useAuthStore } from '@/stores/authStore';

import { useIncomingCallStore } from '../stores/incomingCallStore';

/**
 * Listens for `portal-helpline.incoming` events on the unified realtime
 * channel and pushes them into the incoming-call store so the global
 * IncomingCallDialog can render the ringing UI.
 *
 * No-op for non-clinical roles (the backend never publishes for them, but
 * a defensive role check here keeps the dialog from ever ringing on a
 * client / admin tab if a stray event slips through).
 *
 * Mounted once at the app root. The single subscription is enough for the
 * whole app — `useRealtimeEvent` is a pub/sub registration, not a
 * connection.
 */
export function useIncomingCallListener(): void {
  const role = useAuthStore((s) => s.user?.role);
  const setIncoming = useIncomingCallStore((s) => s.setIncoming);

  const isClinical = !!role && CLINICAL_ROLES.includes(role);

  const handler = useCallback(
    (event: Extract<UserEvent, { type: 'portal-helpline.incoming' }>) => {
      if (!isClinical) return;
      setIncoming({
        sessionId: event.sessionId,
        clientId: event.clientId,
        clientName: event.clientName,
        locationName: event.locationName,
        concerns: event.concerns,
        startedAt: event.startedAt,
        receivedAt: Date.now(),
      });
    },
    [isClinical, setIncoming],
  );

  useRealtimeEvent('portal-helpline.incoming', handler);
}
